import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Input,
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  Skeleton,
  Alert,
  Segmented,
  Empty,
} from 'antd';
import {
  ThunderboltOutlined,
  SearchOutlined,
  FilePdfOutlined,
  FileMarkdownOutlined,
  LinkOutlined,
  BulbOutlined,
  CopyOutlined,
  ExperimentOutlined,
  CheckCircleFilled,
  WarningOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import type { KnowledgeSearchHit } from '@/api/rag';

const { Text, Paragraph } = Typography;

// "What would the agent see for this question?" — the most powerful insight
// for KB authors. Submits the query through a hybrid (vector + BM25 + semantic
// rerank) Azure AI Search call scoped to this KB and renders the top-k hits
// with score bars, source attribution, and excerpts.
//
// Two viewing modes:
//   • Hits      — raw search results (file, page, score, excerpt)
//   • Synthesized — formatted exactly the way the agent prompt receives it,
//                    so the user can see the literal "context block" that
//                    gets injected into the LLM call.

// Suggestion pool — different question shapes nudge the user toward variety.
const SUGGESTIONS = [
  'What is the recommended dose?',
  'When should the patient call the clinic?',
  'List the most common side effects',
  'What does the protocol say about premedication?',
  'Are there any contraindications?',
  'What should we monitor for?',
];

interface TestQueryPanelProps {
  knowledgeId: number;
}

export const TestQueryPanel = observer(function TestQueryPanel({ knowledgeId }: TestQueryPanelProps) {
  const { ragStore: rag, knowledgeDetailStore: kb, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [mode, setMode] = useState<'hits' | 'synthesized'>('hits');

  useEffect(() => {
    rag.resetTest(knowledgeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeId]);

  const showSuggestions = !rag.isRunningTest && rag.testHits.length === 0 && !rag.testError;
  const canRun = rag.testQuery.trim().length > 0 && !rag.isRunningTest;
  const hasContent = kb.sourceCounts.total > 0;
  const hasIndexed = kb.indexedCounts.indexed > 0;

  const sortedHits = useMemo(
    () => [...rag.testHits].sort((a, b) => b.score - a.score),
    [rag.testHits]
  );

  const handleSubmit = (): void => {
    if (!canRun) return;
    trackEvent('Knowledge.TestQueryRun', {
      knowledgeId,
      length: rag.testQuery.length,
    });
    void rag.runTest(knowledgeId, rag.testQuery);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionCard
        hero
        title={
          <span>
            <ExperimentOutlined style={{ marginRight: 6 }} />
            What does the agent retrieve for this question?
          </span>
        }
        subtitle="Runs a hybrid Azure AI Search query (vector + BM25 + semantic rerank) scoped to this KB and shows the top chunks the agent would receive as grounding context."
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input.Search
            placeholder='e.g. "When should the patient hold their dose?"'
            value={rag.testQuery}
            onChange={(e) => rag.setTestQuery(e.target.value)}
            onSearch={handleSubmit}
            enterButton={
              <Button type="primary" icon={<ThunderboltOutlined />} loading={rag.isRunningTest}>
                Run query
              </Button>
            }
            size="large"
            allowClear
            disabled={!hasContent}
            prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
          />

          {!hasContent && (
            <Alert
              type="info"
              showIcon
              icon={<BulbOutlined />}
              message="No sources yet"
              description="Upload PDFs, Markdown, or links in the Sources tab. They become queryable here as soon as they're indexed."
            />
          )}

          {hasContent && !hasIndexed && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message="No indexed sources"
              description="All sources are still being processed by Azure AI Search. Indexing usually takes under a minute. Click Refresh on the Sources tab to check status."
            />
          )}

          {hasContent && hasIndexed && (
            <Tooltip title="Click a suggestion to drop it into the box">
              <Space size={[6, 6]} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Try:
                </Text>
                {SUGGESTIONS.map((s) => (
                  <Tag.CheckableTag
                    key={s}
                    checked={rag.testQuery === s}
                    onChange={() => {
                      rag.setTestQuery(s);
                      void rag.runTest(knowledgeId, s);
                    }}
                    style={{
                      fontSize: 12,
                      padding: '4px 10px',
                      border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                      borderRadius: 999,
                    }}
                  >
                    {s}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </Tooltip>
          )}
        </div>
      </SectionCard>

      {rag.testError && (
        <Alert
          type="error"
          showIcon
          message="Query failed"
          description={
            rag.testError.includes('TestQuery')
              ? 'The /api/Knowledge/TestQuery endpoint has not been deployed yet. The frontend is ready and will light up automatically once the backend ships.'
              : rag.testError
          }
          closable
        />
      )}

      {rag.isRunningTest && (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 4 }} />
        </SectionCard>
      )}

      {!rag.isRunningTest && rag.testHits.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Text style={{ fontSize: 13, fontWeight: 600 }}>
                Top {sortedHits.length} {sortedHits.length === 1 ? 'chunk' : 'chunks'} retrieved
              </Text>
              {rag.testRanAt && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  · {new Date(rag.testRanAt).toLocaleTimeString()}
                </Text>
              )}
            </div>
            <Segmented
              size="small"
              value={mode}
              onChange={(v) => setMode(v as 'hits' | 'synthesized')}
              options={[
                { value: 'hits', label: 'Hits' },
                { value: 'synthesized', label: 'As prompt context' },
              ]}
            />
          </div>

          {mode === 'hits' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sortedHits.map((hit, idx) => (
                <HitCard key={`${hit.documentId}-${idx}`} hit={hit} index={idx} />
              ))}
            </div>
          ) : (
            <SynthesizedView hits={sortedHits} />
          )}
        </>
      )}

      {showSuggestions && hasContent && hasIndexed && (
        <SectionCard>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 13, color: isDark ? colors.slate[400] : colors.slate[500] }}>
                Type a question above (or click a suggestion) to see what the agent would retrieve.
              </span>
            }
          />
        </SectionCard>
      )}
    </div>
  );
});

// ── Single hit card with relevance bar ──────────────────────────────────────

const HitCard = observer(function HitCard({
  hit,
  index,
}: {
  hit: KnowledgeSearchHit;
  index: number;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  // Hybrid/RRF scores typically range 0.01-0.05; show as a relative bar against
  // the max possible score we've seen (0.05 is "great").
  const scorePct = Math.min(100, Math.round((hit.score / 0.05) * 100));

  const fileIcon = hit.fileName?.toLowerCase().endsWith('.pdf') ? (
    <FilePdfOutlined />
  ) : hit.fileName?.toLowerCase().endsWith('.md') ? (
    <FileMarkdownOutlined />
  ) : (
    <LinkOutlined />
  );

  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        padding: '14px 16px',
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: isDark ? `${colors.violet[700]}40` : colors.violet[50],
          color: colors.violet[600],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          flexShrink: 0,
          fontWeight: 600,
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        {index + 1}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: isDark ? colors.slate[50] : colors.slate[900],
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {fileIcon} {hit.citationDisplayName ?? hit.fileName ?? '(unknown source)'}
          </span>
          {hit.pageNumber != null && hit.pageNumber > 0 && (
            <StatusPill tone="slate">Page {hit.pageNumber}</StatusPill>
          )}
          <ScoreBar score={hit.score} pct={scorePct} isDark={isDark} />
          <Tooltip title="Copy excerpt">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                void navigator.clipboard?.writeText(hit.excerpt ?? '');
              }}
            />
          </Tooltip>
        </div>
        <Paragraph
          style={{
            fontSize: 13,
            color: isDark ? colors.slate[200] : colors.slate[700],
            marginTop: 8,
            marginBottom: 0,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {hit.excerpt}
        </Paragraph>
      </div>
    </div>
  );
});

function ScoreBar({ score, pct, isDark }: { score: number; pct: number; isDark: boolean }) {
  // Color graduates: green (>=70%) → amber (40-70%) → slate (<40%)
  const color =
    pct >= 70 ? colors.green[600] : pct >= 40 ? colors.amber[600] : colors.slate[400];
  return (
    <Tooltip title={`Relevance score: ${score.toFixed(4)}`}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 8px',
          background: isDark ? colors.slate[700] : colors.slate[100],
          borderRadius: 999,
        }}
      >
        <CheckCircleFilled style={{ color, fontSize: 10 }} />
        <span
          style={{
            fontSize: 11,
            fontFamily: "'JetBrains Mono', monospace",
            color: isDark ? colors.slate[300] : colors.slate[700],
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {pct}%
        </span>
        <span
          style={{
            display: 'inline-block',
            width: 56,
            height: 4,
            borderRadius: 2,
            background: isDark ? colors.slate[600] : colors.slate[200],
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <span
            style={{
              position: 'absolute',
              inset: 0,
              width: `${pct}%`,
              background: color,
              borderRadius: 2,
            }}
          />
        </span>
      </div>
    </Tooltip>
  );
}

// ── Synthesized view: shows the raw context as it'd be injected to the LLM ──

function SynthesizedView({ hits }: { hits: KnowledgeSearchHit[] }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [copied, setCopied] = useState(false);

  const synth = hits
    .map((h, i) => `[Source ${i + 1}: ${h.citationDisplayName ?? h.fileName ?? 'unknown'}${h.pageNumber ? `, Page ${h.pageNumber}` : ''}]\n${h.excerpt}`)
    .join('\n\n---\n\n');

  return (
    <SectionCard
      title="Prompt context block"
      subtitle="The exact string that gets concatenated into the agent's grounding context. Useful for debugging citation drift."
      actions={
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => {
            void navigator.clipboard?.writeText(synth);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      }
    >
      <pre
        style={{
          margin: 0,
          padding: '14px 16px',
          background: isDark ? colors.slate[900] : colors.slate[50],
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          borderRadius: 8,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12.5,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          color: isDark ? colors.slate[200] : colors.slate[700],
          maxHeight: 480,
          overflow: 'auto',
        }}
      >
        {synth}
      </pre>
    </SectionCard>
  );
}
